/* eslint-disable react-hooks/exhaustive-deps */
import React, {
	useEffect, useMemo, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { GreenroomState } from '../../stores/types/types';
import { ROOM_STATES } from '../../stores/reducers/settings';
import Socket from '../../connection/socket';
import MainVideoLive from './main-video-live';
import MainVideoEditing from './main-video-editing';
import * as SessionActions from '../../stores/actions/session';
import * as VideoActions from '../../stores/actions/video';
import useShots from '../../contexts/shots/provider';
import * as ModalActions from '../../stores/actions/modal';
import { MODAL_TEMPLATES } from '../../stores/reducers/modal';
import SpotlightManager from '../spotlight/spotlight-manager';
import { TitlesProvider } from '../../contexts/titles/titles-context';
import { ProgramViewType } from '../../stores/types/session';
import HoldSlide from './onsite-location/hold-slide';

interface OwnProps {
	socket: Socket | null;
}

export default function MainVideoContainer(props: OwnProps) {
	const currentUser = useSelector((state: GreenroomState) => state.SessionReducer.user);
	const hasUpdatedState = useSelector(
		(state: GreenroomState) => state.SessionReducer.hasUpdatedState,
	);
	const session = useSelector((state: GreenroomState) => state.SessionReducer.session);
	const embed = useSelector((state: GreenroomState) => state.SessionReducer.embed);
	const programView = useSelector((state: GreenroomState) => state.SessionReducer.programView);
	const roomState = useSelector((state: GreenroomState) => state.MeetingReducer.roomState);
	const userIsMuted = useSelector((state: GreenroomState) => state.VideoReducer.audioMuted);
	const modalOpen = useSelector((state: GreenroomState) => state.ModalReducer.modalOpen);
	const shots = useSelector((state: GreenroomState) => state.ShotsReducer.shots);
	const enablerShotId = useSelector((state: GreenroomState) => state.MeetingReducer.enablerShotId);

	const { socket } = props;
	const dispatch = useDispatch();
	const { currentShot } = useShots();

	/* embed view has to have a few seconds of black frames so
	that ffmpeg doesn't get overloaded when it first starts
	de-renders the <MainVideoLive> component */

	const [loadedMediaFeedsCount, setLoadedMediaFeedsCount] = useState<number>(0);

	const enablerShot = useMemo(() => {
		return shots.find((shot) => shot.shot_id === enablerShotId) ?? null;
	}, [enablerShotId, shots]);

	useEffect(() => {
		// We display a "view program feed" modal when a user opens
		// the program feed requiring the user to click to close
		// to avoid Chrome or any browsers autoplay policy from stopping the feed from playing (https://developer.chrome.com/blog/autoplay/)
		if (programView === ProgramViewType.ProgramFeed) {
			dispatch(ModalActions.setModalState({
				modalOpen: true,
				modalTemplate: MODAL_TEMPLATES.PROGRAM_FEED,
			}));
		}
		if (programView === ProgramViewType.OnsiteFeed) {
			dispatch(ModalActions.setModalState({
				modalOpen: true,
				modalTemplate: MODAL_TEMPLATES.ONSITE_FEED,
			}));
		}
	}, []);

	useEffect(() => {
		if (!hasUpdatedState && session) {
			socket?.sendMessage('request-state', session);
			dispatch(SessionActions.setHasUpdatedState(true));
		}
	}, [hasUpdatedState, currentUser]);

	useEffect(() => {
		if (roomState && !userIsMuted) {
			dispatch(VideoActions.resetAudioMutedPrefs());
		}
	}, [dispatch, roomState, userIsMuted]);

	const iterateLoadedMediaCount = () => {
		setLoadedMediaFeedsCount(loadedMediaFeedsCount + 1);
	};

	// onsite and program feed must dismiss a modal before displaying MainVideoLive
	// embed view must have a server state of started before fully displaying
	//		when not, display an overlay to better allow bullman to startup
	return (
		<TitlesProvider
			titlesOn={enablerShot ? enablerShot.shot_data.videoTitlesOn : currentShot.shot_data.videoTitlesOn}
			shotId={enablerShot ? enablerShot.shot_id : currentShot.shot_id}
			isCanvas={true}
		>
			{embed && !enablerShot && <>
				{
					// if program view is program feed or onsite feed
					// do not render until the modal is closed
					(
						programView === ProgramViewType.None
						|| programView === ProgramViewType.OnsiteLocation
						|| !modalOpen
					)
					&& roomState === ROOM_STATES.SHOWTIME
						? (
							<>
								<div id='hidden-spotlight' className={classNames('spotlights', 'spotlight-container')} style={{ position: 'absolute', height: '1080px', width: '1920px' }}>
									<SpotlightManager socket={socket} iterateLoadedMediaCount={() => {}}/>
								</div>
								<MainVideoLive socket={props.socket} />
							</>
						) : (
							<HoldSlide
								showHoldSlide={true}
								showVideoWrapper={programView !== ProgramViewType.OnsiteLocation}
							/>
						)
				}
			</>}

			{enablerShot && (
				<MainVideoEditing
					socket={props.socket}
					shotBeingEdited={enablerShot}
					isForShotEditorModal={false}
					iterateLoadedMediaCount={iterateLoadedMediaCount}
				/>
			)}

			{!embed && <>
				{roomState === ROOM_STATES.SHOWTIME
					&& <>
						<div
							id='hidden-spotlight'
							className={classNames('spotlights', 'spotlight-container')}
							style={{ position: 'absolute', height: '1080px', width: '1920px' }}
						>
							<SpotlightManager socket={socket} iterateLoadedMediaCount={() => {}}/>
						</div>
						<MainVideoLive
							socket={props.socket}
						/>
					</>
				}

				{roomState === ROOM_STATES.EDITING
					&& <MainVideoEditing
						socket={props.socket}
						shotBeingEdited={currentShot}
						isForShotEditorModal={false}
						iterateLoadedMediaCount={iterateLoadedMediaCount}
					/>
				}
			</>}

			{!embed && <div id="media-modal-portal" />}

		</TitlesProvider>
	);
}
